import { SimpleGrid } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { IconSchool } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '@/components/ui/PageHeader';
import { CardGridSkeleton, EmptyState, ErrorState } from '@/components/ui/StateViews';
import { TrainingMaterialCard } from '@/components/domain/TrainingMaterialCard';
import { CatalogLockedOffer } from '@/features/access/PlanOffer';
import { lockedCopy } from '@/features/content/accessCopy';
import { TRAINING_KEY, fetchTrainingMaterials } from '@/features/content/api';

/**
 * Learning catalogue (§45).
 *
 * Locked materials stay on the page, dimmed and explained, rather than being
 * hidden — the owner needs to see what the access rules actually do (§19).
 */
export function TrainingListPage() {
  const navigate = useNavigate();
  const query = useQuery({ queryKey: TRAINING_KEY, queryFn: fetchTrainingMaterials });

  const items = query.data?.items ?? [];

  return (
    <>
      <PageHeader
        title="Обучение"
        description="Материалы для ведущего: тексты, видео и ссылки. Открываются по вашему доступу."
      />

      {query.isLoading ? (
        <CardGridSkeleton count={3} />
      ) : query.isError ? (
        <ErrorState onRetry={() => void query.refetch()} />
      ) : items.length === 0 ? (
        <EmptyState
          icon={<IconSchool size={28} stroke={1.5} />}
          title="Материалов пока нет"
          description="Опубликованные материалы появятся здесь."
        />
      ) : (
        <>
          <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="lg">
            {items.map((item) => {
              const locked = lockedCopy(item.access);
              return (
                <TrainingMaterialCard
                  key={item.id}
                  title={item.title}
                  description={item.description}
                  coverImage={item.coverImage}
                  blockTypes={item.blockTypes}
                  lockedReason={locked?.reason ?? null}
                  lockedHint={locked?.hint ?? null}
                  onClick={() => void navigate(`/training/${item.slug}`)}
                />
              );
            })}
          </SimpleGrid>

          <CatalogLockedOffer items={items.map((item) => item.access)} />
        </>
      )}
    </>
  );
}
